import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, asArray, formatApiError } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import { ArrowLeft, BookOpen, Search } from "lucide-react";

/**
 * Read-only knowledge base for the client portal (`/portal/knowledge` and
 * `/portal/knowledge/:id`). Deliberately its own small component rather than
 * a reuse of `Knowledge.jsx`/`ArticleDetail.jsx`: those carry create/edit/
 * archive controls gated to `desk_content.CONTENT_MANAGE` staff roles, and
 * "guard the write, hide the button too" means a customer should never see
 * an affordance the server would 403 anyway.
 *
 * `kb_articles` carries no `client_id` and no staff/customer visibility
 * flag -- one tenant-wide shelf, the same rows staff read
 * (`authz.PORTAL_ALLOW`'s own comment on the three GET routes this page
 * calls). Nothing here is scoped per client because there is nothing on the
 * article to scope by.
 */
const PAGE = 20;

export default function PortalKnowledge() {
  const { id } = useParams();
  return id ? <PortalArticle id={id} /> : <PortalKnowledgeList />;
}

function PortalKnowledgeList() {
  const [params, setParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [body, setBody] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const offset = Number(params.get("offset") || 0);

  const set = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    if (k !== "offset") next.delete("offset");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/kb/categories").then((r) => setCategories(asArray(r.data))).catch(() => setCategories([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    qs.set("limit", String(PAGE));
    qs.set("offset", String(offset));
    api.get(`/kb?${qs}`)
      .then((r) => {
        setErr("");
        setBody(r.data && typeof r.data === "object" && r.data.items
          ? r.data : { items: asArray(r.data), total: asArray(r.data).length });
      })
      .catch((e) => { setErr(formatApiError(e)); setBody({ items: [], total: 0 }); })
      .finally(() => setLoading(false));
  }, [q, category, offset]);

  useEffect(() => { load(); }, [load]);

  const pages = Math.max(1, Math.ceil((body.total || 0) / PAGE));
  const page = Math.floor(offset / PAGE) + 1;

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Client Portal</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1 flex items-center gap-2">
            <BookOpen size={22} className="text-blue-400" /> Knowledge base
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Guides and answers to common questions.</p>
        </div>
        <Link to="/portal" className="border border-zinc-800 text-zinc-300 rounded-md px-3 py-1.5 text-sm hover:border-zinc-700">
          Portal overview
        </Link>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input defaultValue={q}
                 onKeyDown={(e) => e.key === "Enter" && set("q", e.target.value)}
                 onBlur={(e) => set("q", e.target.value)}
                 placeholder="Search articles, then Enter"
                 data-testid="portal-kb-search"
                 className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-full focus:outline-none focus:border-blue-500/60" />
        </div>
        <select value={category} onChange={(e) => set("category", e.target.value)}
                data-testid="portal-kb-category"
                className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm min-w-0 max-w-[12rem] shrink">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {err && <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded-md px-4 py-3 text-sm">{err}</div>}

      <div className="grid gap-3">
        {loading && <div className="text-sm text-zinc-500">Loading…</div>}
        {!loading && !body.items.length && (
          <div className="text-sm text-zinc-500" data-testid="portal-kb-empty">
            {q || category ? "No articles match this search." : "No articles yet."}
          </div>
        )}
        {!loading && body.items.map((a) => (
          <Link key={a.id} to={`/portal/knowledge/${a.id}`} data-testid={`portal-kb-article-${a.id}`}
                className="block bg-zinc-950 border border-zinc-900 rounded-md p-4 hover:border-zinc-800 transition-colors">
            <div className="font-display font-medium text-zinc-100">{a.title}</div>
            <div className="flex items-center gap-2 mt-1.5">
              <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 border border-zinc-800 rounded px-1.5 py-0.5">
                {a.category || "General"}
              </span>
              {asArray(a.tags).slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] text-zinc-600">#{t}</span>
              ))}
            </div>
          </Link>
        ))}
      </div>

      {pages > 1 && (
        <div className="flex items-center gap-3 text-sm">
          <button disabled={offset <= 0} onClick={() => set("offset", String(Math.max(0, offset - PAGE)))}
                  className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Previous</button>
          <span className="text-zinc-500 font-mono text-xs">Page {page} of {pages} · {body.total} articles</span>
          <button disabled={page >= pages} onClick={() => set("offset", String(offset + PAGE))}
                  className="border border-zinc-800 rounded px-3 py-1 disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

function PortalArticle({ id }) {
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/kb/${id}`)
      .then((r) => { setErr(""); setA(r.data); })
      .catch((e) => { setErr(formatApiError(e)); setA(null); });
  }, [id]);

  if (err) {
    return (
      <div className="space-y-4">
        <Link to="/portal/knowledge" className="text-sm text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1.5">
          <ArrowLeft size={14} /> Back to knowledge base
        </Link>
        <div className="border border-red-900/50 bg-red-950/30 text-red-300 rounded-md px-4 py-3 text-sm">{err}</div>
      </div>
    );
  }
  if (!a) return <div className="text-zinc-500 text-sm">Loading article…</div>;

  return (
    <div className="space-y-4 max-w-3xl">
      <Link to="/portal/knowledge" data-testid="portal-kb-back"
            className="text-sm text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1.5">
        <ArrowLeft size={14} /> Back to knowledge base
      </Link>
      <div>
        <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 border border-zinc-800 rounded px-1.5 py-0.5">
          {a.category || "General"}
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight mt-2">{a.title}</h1>
      </div>
      <div className="bg-zinc-950 border border-zinc-900 rounded-md p-6 prose prose-invert prose-sm max-w-none"
           data-testid="portal-kb-article-body"
           dangerouslySetInnerHTML={{ __html: renderMarkdown(a.body_md) }} />
    </div>
  );
}
